import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, Mail, Phone, MapPin, Building, CheckCircle, XCircle, Edit, Save, Lock, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { GooglePlacesAddress } from "./GooglePlacesAddress";
import { PasswordResetDialog } from "./PasswordResetDialog";

interface ContactInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentProfile: any;
  onProfileUpdate: () => void;
}

export const ContactInfoDialog = ({ 
  open, 
  onOpenChange, 
  currentProfile,
  onProfileUpdate 
}: ContactInfoDialogProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    company_name: ""
  });
  const { toast } = useToast();

  useEffect(() => {
    if (currentProfile) {
      setFormData({
        full_name: currentProfile.full_name || "",
        email: currentProfile.email || "",
        phone: currentProfile.phone || "",
        address: currentProfile.address || "",
        company_name: currentProfile.company_name || ""
      });
    }
  }, [currentProfile]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!currentProfile) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: formData.full_name,
          phone: formData.phone,
          address: formData.address,
          company_name: formData.company_name
        })
        .eq("id", currentProfile.id);

      if (error) throw error;

      toast({
        title: "Contact information updated",
        description: "Your contact details have been successfully updated.",
      });

      setIsEditing(false);
      onProfileUpdate();
    } catch (error: any) {
      toast({
        title: "Error updating contact information",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    if (currentProfile) {
      setFormData({
        full_name: currentProfile.full_name || "",
        email: currentProfile.email || "",
        phone: currentProfile.phone || "",
        address: currentProfile.address || "",
        company_name: currentProfile.company_name || ""
      });
    }
    setIsEditing(false);
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const { error } = await supabase.functions.invoke('delete-user-account', {
        method: 'POST'
      });

      if (error) throw error;

      toast({
        title: "Account deleted",
        description: "Your account has been permanently deleted.",
      });

      // Sign out the user
      await supabase.auth.signOut();
      
      // Close dialog and redirect will happen automatically via auth state change
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error deleting account",
        description: error.message || "Failed to delete account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto"
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement;
          if (target?.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          const target = e.target as HTMLElement;
          if (target?.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Contact Information
          </DialogTitle>
          <DialogDescription>
            View and manage your contact details. Keep your information up to date for better communication.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Verification Status Card */}
          <Card className="border-l-4 border-l-primary">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Verification Status</CardTitle>
              <CardDescription>
                Verification helps build trust and improves communication
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  {currentProfile?.email_verified ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600" />
                  )}
                  <span className="text-sm">Email Verified</span>
                  <Badge variant={currentProfile?.email_verified ? "default" : "destructive"} className="text-xs">
                    {currentProfile?.email_verified ? "Verified" : "Unverified"}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  {currentProfile?.phone_verified ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600" />
                  )}
                  <span className="text-sm">Phone Verified</span>
                  <Badge variant={currentProfile?.phone_verified ? "default" : "destructive"} className="text-xs">
                    {currentProfile?.phone_verified ? "Verified" : "Unverified"}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Details */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Contact Details</CardTitle>
                <CardDescription>
                  Your personal and contact information
                </CardDescription>
              </div>
              {!isEditing ? (
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="outline"
                  size="sm"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={handleSave}
                    disabled={isLoading}
                    size="sm"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isLoading ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                    size="sm"
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="full_name" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Full Name
                </Label>
                {isEditing ? (
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => handleInputChange("full_name", e.target.value)}
                    placeholder="Enter your full name"
                  />
                ) : (
                  <div className="px-3 py-2 bg-muted rounded-md">
                    {formData.full_name || "Not provided"}
                  </div>
                )}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Address
                  {currentProfile?.email_verified && (
                    <CheckCircle className="h-3 w-3 text-green-600" />
                  )}
                </Label>
                <div className="px-3 py-2 bg-muted rounded-md text-muted-foreground">
                  {formData.email || "Not provided"}
                  <span className="text-xs block mt-1">
                    Email cannot be changed here. Contact support if needed.
                  </span>
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number
                  {currentProfile?.phone_verified && (
                    <CheckCircle className="h-3 w-3 text-green-600" />
                  )}
                </Label>
                {isEditing ? (
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="Enter your phone number"
                    type="tel"
                  />
                ) : (
                  <div className="px-3 py-2 bg-muted rounded-md">
                    {formData.phone || "Not provided"}
                  </div>
                )}
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label htmlFor="address" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Address
                </Label>
                {isEditing ? (
                  <GooglePlacesAddress
                    id="address"
                    value={formData.address}
                    onChange={(value) => handleInputChange("address", value)}
                    placeholder="Enter your address"
                  />
                ) : (
                  <div className="px-3 py-2 bg-muted rounded-md">
                    {formData.address || "Not provided"}
                  </div>
                )}
              </div>

              {/* Company Name (if applicable) */}
              <div className="space-y-2">
                <Label htmlFor="company_name" className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Company Name (Optional)
                </Label>
                {isEditing ? (
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => handleInputChange("company_name", e.target.value)}
                    placeholder="Enter company name if applicable"
                  />
                ) : (
                  <div className="px-3 py-2 bg-muted rounded-md">
                    {formData.company_name || "Not provided"}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Account Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Account Information</CardTitle>
              <CardDescription>
                Additional details about your account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center py-2">
                <span className="font-medium">User Type</span>
                <Badge variant="outline">{currentProfile?.user_type || "customer"}</Badge>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="font-medium">Account Created</span>
                <span className="text-sm text-muted-foreground">
                  {currentProfile?.created_at ? 
                    new Date(currentProfile.created_at).toLocaleDateString() : 
                    "Unknown"
                  }
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="font-medium">Last Updated</span>
                <span className="text-sm text-muted-foreground">
                  {currentProfile?.updated_at ? 
                    new Date(currentProfile.updated_at).toLocaleDateString() : 
                    "Unknown"
                  }
                </span>
              </div>
              
              {/* Password Change Button */}
              <div className="flex justify-between items-center py-2 border-t pt-4">
                <div>
                  <span className="font-medium">Password</span>
                  <p className="text-sm text-muted-foreground">Change your account password</p>
                </div>
                <Button
                  onClick={() => setShowPasswordReset(true)}
                  variant="outline"
                  size="sm"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Change Password
                </Button>
              </div>

              {/* Delete Account Button */}
              <div className="flex justify-between items-center py-2 border-t pt-4">
                <div>
                  <span className="font-medium text-destructive">Delete Account</span>
                  <p className="text-sm text-muted-foreground">Permanently delete your account and all data</p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {isDeleting ? "Deleting..." : "Delete Account"}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                      <AlertDialogDescription className="space-y-2">
                        <p>This action cannot be undone. This will permanently delete your account and remove all your data from our servers, including:</p>
                        <ul className="list-disc list-inside text-sm space-y-1 ml-4">
                          <li>Your profile information</li>
                          <li>All job history and messages</li>
                          <li>Ratings and reviews</li>
                          <li>Portfolio photos and certificates</li>
                          <li>Payment history</li>
                        </ul>
                        <p className="font-medium">This action is irreversible.</p>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteAccount}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        disabled={isDeleting}
                      >
                        {isDeleting ? "Deleting..." : "Yes, delete my account"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end pt-4">
          <Button onClick={() => onOpenChange(false)} variant="outline">
            Close
          </Button>
        </div>

        {/* Password Reset Dialog */}
        <PasswordResetDialog
          open={showPasswordReset}
          onOpenChange={setShowPasswordReset}
        />
      </DialogContent>
    </Dialog>
  );
};